/**
 * Filters for @rerun-io/web-viewer WASM errors that are not actionable.
 *
 * Five classes of error are filtered:
 *
 * 1. **Closure teardown** -- after viewer.stop() frees the WASM module,
 *    pending async callbacks invoke freed closures. Depending on the
 *    wasm-bindgen version this surfaces as either:
 *    - "Cannot read properties of null (reading 'closure<N>_externref_shim')"
 *    - "Cannot read properties of null (reading 'wasm_bindgen__convert__closures_____invoke__h<hash>')"
 *
 * 2. **Stopped viewer** -- polling code queries a viewer whose internal
 *    WASM state has transitioned to stopped (e.g. gRPC stream ended),
 *    producing "attempted to get active timeline in a stopped web viewer".
 *
 * 3. **WASM runtime panics** -- Rust panics inside the Rerun WASM binary
 *    surface as the standard WASM trap, with browser-specific wording:
 *    - Chrome/Edge:  `RuntimeError: unreachable`
 *    - Firefox:      `RuntimeError: unreachable executed`
 *    - Safari:       `RuntimeError: Unreachable code should not be
 *                    executed (evaluating '<expr>')`
 *    These originate from wgpu/egui internals (e.g. GPU buffer mapping
 *    failures when the browser loses GPU context, or a double-panic
 *    during WebViewer.stop() teardown) and have no in-app stack frames.
 *
 * 4. **WASM instantiation failures** -- wasm-bindgen import mismatches
 *    produce `WebAssembly.instantiate(): Import #0 "wbg"` TypeErrors
 *    when the WASM binary and JS glue versions are out of sync (e.g.
 *    stale CDN cache). The stack originates entirely within the Rerun
 *    package and is not actionable by application code.
 *
 * 5. **ESM MIME type rejection (Safari)** -- Rerun's WASM bootstrap
 *    issues dynamic ESM imports (workers, glue modules). When the CDN
 *    or workspace proxy returns an HTML error page (404, redirect,
 *    captive-portal style auth wall) instead of JS, Safari strictly
 *    rejects with `TypeError: 'text/html' is not a valid JavaScript
 *    MIME type.`. The promise from `viewer.start()` rejects with this
 *    error. The viewer.start() caller observes this rejection directly;
 *    beforeSend only drops matching Sentry events when a Rerun failure
 *    breadcrumb confirms the source.
 */

const RERUN_CLOSURE_PATTERN = /closure\d+_externref_shim/;
const WASM_BINDGEN_CLOSURE_PATTERN = /wasm_bindgen__convert__closures/;
const RERUN_STOPPED_VIEWER_PATTERN = /in a stopped web viewer/;
const RERUN_WASM_FRAME_PATTERN = /re_viewer|@rerun-io\/web-viewer/;
const RERUN_WASM_INSTANTIATE_PATTERN = /WebAssembly\.instantiate\(\).*"wbg"/;
const SAFARI_ESM_MIME_PATTERN = /is not a valid JavaScript MIME type/;
const RERUN_BREADCRUMB_MESSAGE_PATTERN = /WebViewer failure|Failed to load rerun|viewer\.start\(\) failed/;

// WASM "unreachable" trap message varies by browser:
//   Chrome/Edge: "unreachable"
//   Firefox:     "unreachable executed"
//   Safari:      "Unreachable code should not be executed (evaluating '<expr>')"
const WASM_UNREACHABLE_PATTERN = /^(unreachable( executed)?|Unreachable code should not be executed)/;

/**
 * Check whether an unhandled rejection reason is a Rerun WASM
 * teardown or stopped-viewer error that should be silently suppressed.
 */
export function isRerunWasmTeardownError(reason: unknown): boolean {
  if (!(reason instanceof Error)) return false;

  // Closure teardown TypeError (both legacy and wasm-bindgen naming)
  if (RERUN_CLOSURE_PATTERN.test(reason.message)) return true;
  if (WASM_BINDGEN_CLOSURE_PATTERN.test(reason.message)) return true;

  // Stopped viewer errors
  if (RERUN_STOPPED_VIEWER_PATTERN.test(reason.message)) return true;

  // WASM instantiation failure from wasm-bindgen import mismatch
  if (RERUN_WASM_INSTANTIATE_PATTERN.test(reason.message)) {
    return RERUN_WASM_FRAME_PATTERN.test(reason.stack ?? '');
  }

  // RuntimeError: unreachable from Rerun WASM double-panic during destroy
  if (reason instanceof WebAssembly.RuntimeError && WASM_UNREACHABLE_PATTERN.test(reason.message)) {
    return RERUN_WASM_FRAME_PATTERN.test(reason.stack ?? '');
  }

  return false;
}

/**
 * Check whether a synchronous error is a Rerun WASM runtime panic.
 *
 * WASM traps surface as `RuntimeError: unreachable` thrown synchronously
 * (e.g. inside requestAnimationFrame). We verify the stack trace
 * references `re_viewer` to avoid suppressing unrelated WASM errors.
 */
export function isRerunWasmRuntimeError(error: unknown): boolean {
  if (!(error instanceof WebAssembly.RuntimeError)) return false;
  if (!WASM_UNREACHABLE_PATTERN.test(error.message)) return false;
  return RERUN_WASM_FRAME_PATTERN.test(error.stack ?? '');
}

/**
 * Check whether an error is Safari's strict ESM MIME type rejection.
 *
 * RerunViewer uses this at the call site where `viewer.start()` is the
 * confirmed source. Sentry beforeSend still requires a Rerun failure
 * breadcrumb before dropping stackless events with the same browser
 * message.
 */
export function isSafariEsmMimeTypeError(error: unknown): boolean {
  return error instanceof Error && SAFARI_ESM_MIME_PATTERN.test(error.message);
}

/**
 * Strip signed query params and fragments before including Rerun URLs in
 * Sentry breadcrumbs or event extras.
 */
export function redactRerunUrlForSentry(rawUrl: string): string {
  const rerunPrefix = rawUrl.startsWith('rerun+') ? 'rerun+' : '';
  const parseableUrl = rerunPrefix ? rawUrl.slice(rerunPrefix.length) : rawUrl;

  try {
    const parsed = new URL(parseableUrl);
    return `${rerunPrefix}${parsed.origin}${parsed.pathname}`;
  } catch {
    return rawUrl.split(/[?#]/, 1)[0] || '<invalid-url>';
  }
}

/**
 * Check whether a Sentry event's exception values contain a Rerun
 * WASM error (teardown, stopped viewer, or runtime panic), indicating
 * the event should be dropped.
 */
export function isRerunWasmSentryEvent(
  exceptionValues: {
    type?: string;
    value?: string;
    stacktrace?: { frames?: { filename?: string; module?: string }[] };
  }[],
  breadcrumbs: { category?: string; message?: string }[] = []
): boolean {
  const hasRerunFailureBreadcrumb = breadcrumbs.some((breadcrumb) =>
    RERUN_BREADCRUMB_MESSAGE_PATTERN.test(breadcrumb.message ?? '')
  );

  return exceptionValues.some((ex) => {
    // Closure teardown TypeError (both legacy and wasm-bindgen naming)
    if (RERUN_CLOSURE_PATTERN.test(ex.value ?? '')) return true;
    if (WASM_BINDGEN_CLOSURE_PATTERN.test(ex.value ?? '')) return true;

    // Stopped viewer errors
    if (RERUN_STOPPED_VIEWER_PATTERN.test(ex.value ?? '')) return true;

    // WASM instantiation failure from wasm-bindgen import mismatch
    if (RERUN_WASM_INSTANTIATE_PATTERN.test(ex.value ?? '')) {
      const frames = ex.stacktrace?.frames;
      if (frames && frames.length > 0) {
        return frames.some(
          (f) => RERUN_WASM_FRAME_PATTERN.test(f.filename ?? '') || RERUN_WASM_FRAME_PATTERN.test(f.module ?? '')
        );
      }
    }

    // RuntimeError: unreachable originating from Rerun WASM modules
    if (ex.type === 'RuntimeError' && WASM_UNREACHABLE_PATTERN.test(ex.value ?? '')) {
      const frames = ex.stacktrace?.frames;
      if (frames && frames.length > 0) {
        return frames.some(
          (f) => RERUN_WASM_FRAME_PATTERN.test(f.filename ?? '') || RERUN_WASM_FRAME_PATTERN.test(f.module ?? '')
        );
      }
    }

    // Safari ESM MIME type rejection -- emitted by Safari's JSC ESM
    // loader when Rerun's bootstrap imports a module that responds with
    // text/html. The exception often has no stack frames, so require a
    // Rerun failure breadcrumb to avoid hiding unrelated app dynamic-
    // import failures after a user merely opened a Rerun viewer.
    if (SAFARI_ESM_MIME_PATTERN.test(ex.value ?? '')) return hasRerunFailureBreadcrumb;

    return false;
  });
}
